
import { useEffect, useState } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { getContactInfo, sendContactMessage } from '../../api/apiServices';

export default function ContactPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState('idle');

  const [contact, setContact] = useState([])
    const [contactId, setContactId] = useState(null);
    const [loading, setLoading] = useState(true);
  
    useEffect(() => {
        const fetchContact = async () => {
          try {
            const res = await getContactInfo();
    
            if (res?.data) {
              setContact(res.data);
            }
          } catch (error) {
            console.error("Fetch Contact Error:", error);
          } finally {
            setLoading(false);
          }
        };
    
        fetchContact();
      }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
  
    try {
      setIsSubmitting(true);
      setSubmitStatus('idle')
  
      await sendContactMessage({
        name: formData.name,
        email: formData.email,
        subject: formData.subject,
        message: formData.message,
      });
  
      setSubmitStatus("success");
      setFormData({
        name: "",
        email: "",
        subject: "",
        message: "",
      });
    } catch (error) {
      console.error(error);
      setSubmitStatus("error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: 'ri-phone-line',
      title: `${contact?.hotlineNote}`,
      details: [`${contact?.phone}`, 'Available around the clock'],
      highlight: true
    },
    {
      icon: 'ri-mail-line',
      title: 'Email Us',
      details: [`${contact?.email}`],
      highlight: false
    },
    {
      icon: 'ri-map-pin-line',
      title: 'Visit Us',
      details: [`${contact?.address}`],
      highlight: false
    },
    {
      icon: 'ri-time-line',
      title: 'Office Hours',
      details: ['Monday - Friday: 9am - 6pm', 'Saturday: 10am - 2pm'],
      highlight: false
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative pt-20 sm:pt-24">
        <div className="bg-gradient-to-r from-[#6a4bc3] to-[#8a6bd3] py-16 sm:py-20 px-4 sm:px-6 lg:px-20">
          <div className="max-w-7xl mx-auto text-center">
            <p className="text-white/80 text-sm mb-3">GET IN TOUCH</p>
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4">
              Contact Us
            </h1>
            <p className="text-white/90 text-base sm:text-lg max-w-2xl mx-auto">
              Whether you need support, want to volunteer, or have questions about our programs, we're here to help.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="py-12 px-4 sm:px-6 lg:px-20 -mt-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
            {contactInfo.map((info, index) => (
              <div
                key={index}
                className={`rounded-2xl p-5 sm:p-6 ${
                  info.highlight
                    ? 'bg-[#c9a24d] text-white'
                    : 'bg-white shadow-lg border border-gray-100'
                }`}
              >
                <div className={`w-12 h-12 rounded-full flex items-center justify-center mb-4 ${
                  info.highlight ? 'bg-white/20' : 'bg-[#6a4bc3]/10'
                }`}>
                  <i className={`${info.icon} text-xl ${info.highlight ? 'text-white' : 'text-[#6a4bc3]'}`}></i>
                </div>
                <h3 className={`font-semibold mb-2 ${info.highlight ? 'text-white' : 'text-gray-900'}`}>
                  {info.title}
                </h3>
                {info.details.map((detail, i) => (
                  <p key={i} className={`text-sm ${info.highlight ? 'text-white/90' : 'text-gray-600'}`}>
                    {detail}
                  </p>
                ))}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form & Map Section */}
      <section className="py-12 sm:py-16 px-4 sm:px-6 lg:px-20">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12">
            {/* Contact Form */}
            <div>
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-6">
                Send Us a Message
              </h2>
              <p className="text-gray-600 mb-8">
                Fill out the form below and we'll get back to you as soon as possible.
              </p>
              
              <form id="contact-page-form" data-readdy-form onSubmit={handleSubmit} className="space-y-5">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Your Name *
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#6a4bc3] text-sm"
                      placeholder="Enter your name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#6a4bc3] text-sm"
                      placeholder="your.email@example.com"
                    />
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#6a4bc3] text-sm"
                      placeholder="(123) 456-7890"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Subject *
                    </label>
                    <select
                      name="subject"
                      value={formData.subject}
                      onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#6a4bc3] text-sm cursor-pointer"
                    >
                      <option value="">Select a subject</option>
                      <option value="General Inquiry">General Inquiry</option>
                      <option value="Need Support">Need Support</option>
                      <option value="Volunteer">Volunteer</option>
                      <option value="Donation">Donation</option>
                      <option value="Partnership">Partnership</option>
                      <option value="Media">Media Inquiry</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Message *
                  </label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={(e) => {
                      if (e.target.value.length <= 500) {
                        setFormData({ ...formData, message: e.target.value });
                      }
                    }}
                    required
                    rows={5}
                    maxLength={500}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#6a4bc3] text-sm resize-none"
                    placeholder="How can we help you?"
                  />
                  <p className="text-xs text-gray-500 mt-1">{formData.message.length}/500 characters</p>
                </div>

                {submitStatus === 'success' && (
                  <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg text-sm">
                    Thank you for reaching out! We'll get back to you soon.
                  </div>
                )}

                {submitStatus === 'error' && (
                  <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg text-sm">
                    Something went wrong. Please try again.
                  </div>
                )}

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-[#6a4bc3] text-white py-4 rounded-full font-semibold hover:bg-[#5a3ba3] transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                >
                  {isSubmitting ? 'Sending...' : 'Send Message'}
                </button>
              </form>
            </div>

            {/* Map & Additional Info */}
            <div>
              <div className="rounded-2xl overflow-hidden h-[300px] sm:h-[350px] mb-6">
                <iframe
                  src={contact?.mapUrl}
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Our Location"
                ></iframe>
              </div>

              <div className="bg-[#f8f6fc] rounded-2xl p-6 sm:p-8">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Follow Our Journey</h3>
                <p className="text-gray-600 text-sm mb-6">
                  Stay connected with us on social media for updates, stories, and ways to get involved.
                </p>
                <div className="flex gap-3">
                  <a href={contact?.facebook} className="w-10 h-10 bg-[#6a4bc3] rounded-full flex items-center justify-center text-white hover:bg-[#c9a24d] transition-colors cursor-pointer">
                    <i className="ri-facebook-fill text-lg"></i>
                  </a>
                  <a href={contact?.twitter} className="w-10 h-10 bg-[#6a4bc3] rounded-full flex items-center justify-center text-white hover:bg-[#c9a24d] transition-colors cursor-pointer">
                    <i className="ri-twitter-x-fill text-lg"></i>
                  </a>
                  <a href={contact?.instagram} className="w-10 h-10 bg-[#6a4bc3] rounded-full flex items-center justify-center text-white hover:bg-[#c9a24d] transition-colors cursor-pointer">
                    <i className="ri-instagram-fill text-lg"></i>
                  </a>
                  <a href={contact.linkedin} className="w-10 h-10 bg-[#6a4bc3] rounded-full flex items-center justify-center text-white hover:bg-[#c9a24d] transition-colors cursor-pointer">
                    <i className="ri-linkedin-fill text-lg"></i>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
